import { create } from 'zustand';

/**
 * 036 (issue 208, FR-024, research R-008) — "records in a small Zustand store that this URL is
 * being shown from storage, with its moment and the reason." One entry per URL, written by
 * `storing-client.ts` and read by whichever screen asked for that URL — every readable screen now
 * (home, the contexts/projects/tags lists, a single context/project/tag, review, recurring and
 * tickler), through `useShownFromStorage`; this comment said "home is this slice's only reader"
 * until issue 258 (B4) noticed it was stale.
 *
 * In memory only, never persisted: it describes what THIS PAGE LOAD is currently showing, not a
 * fact about the device. A reload starts with nothing shown from storage, which is correct — the
 * next load decides fresh whether anything had to fall back.
 */
export type StoredViewReason = 'unreachable' | 'failing';

export interface ShownFromStorage {
  /** The server's own `Date` header, verbatim (FR-018) — `shared/actions/model/dates.ts` formats it. */
  readonly moment: string;
  readonly reason: StoredViewReason;
}

interface PresentationState {
  readonly shown: Readonly<Record<string, ShownFromStorage>>;
}

const store = create<PresentationState>()(() => ({ shown: {} }));

/**
 * 036 (issue 258, B4) — what `clearShownFromStorage` most recently removed for each url, `null` when
 * the most recent clear found nothing to remove. Kept so `restoreLastCleared` below can put back
 * EXACTLY the record a since-discarded quiet refresh's own on-time success cleared, without its
 * caller (`core/reach/quiet-refresh.ts`'s `busyDiscard`) needing to have captured a snapshot of its
 * own beforehand. Always overwritten on every clear, even a no-op one — a STALE entry is exactly
 * what would let a later, unrelated discard restore a mark that has nothing to do with it.
 */
const lastCleared = new Map<string, ShownFromStorage | null>();

/** `storing-client.ts`, the moment a read falls back to a stored body. */
export function markShownFromStorage(url: string, record: ShownFromStorage): void {
  store.setState((s) => ({ shown: { ...s.shown, [url]: record } }));
}

/** `storing-client.ts`, the moment a URL is answered live again (FR-022). */
export function clearShownFromStorage(url: string): void {
  store.setState((s) => {
    lastCleared.set(url, s.shown[url] ?? null);
    if (!(url in s.shown)) return s;
    const rest = { ...s.shown };
    delete rest[url];
    return { shown: rest };
  });
}

/**
 * 036 (issue 258, B4, FR-022) — "the mark clears only when a screen applies the fresh body," the
 * other half: `storing-client.ts`'s on-time GET success already clears a mark before
 * `core/reach/quiet-refresh.ts`'s `readOnce()` decides the answer cannot actually be shown
 * (`busy()`); a port's `busyDiscard` calls this, with the SAME url list it reads its own stored view
 * from, to put it back.
 *
 * Restores a url ONLY when `lastCleared` holds a real record for it AND it is STILL absent from
 * `shown` right now — never a url nothing was ever cleared for (nothing to restore), and never one
 * some other, unrelated process has since marked or legitimately cleared again (not this call's to
 * touch). Scoped to the caller's OWN url list rather than every url this module has ever seen, so one
 * screen's busy-discard can never restore a mark that belongs to a different screen's own reads.
 */
export function restoreLastCleared(urls: readonly string[]): void {
  store.setState((s) => {
    let changed = false;
    const next = { ...s.shown };
    for (const url of urls) {
      const prior = lastCleared.get(url);
      if (prior && !(url in s.shown)) {
        next[url] = prior;
        changed = true;
      }
    }
    return changed ? { shown: next } : s;
  });
}

export interface StoredViewSummary {
  /** True while at least one of `urls` is currently shown from storage. */
  readonly any: boolean;
  /**
   * The OLDEST of the shown records among `urls` (contract: "where a screen is made of several
   * answers, the oldest is shown"), or `null` when `any` is false. Compared as instants — the
   * server's `Date` header carries one-second resolution, which is ample for "which of these five
   * answers is stalest".
   */
  readonly oldest: ShownFromStorage | null;
}

function summarize(shown: Readonly<Record<string, ShownFromStorage>>, urls: readonly string[]): StoredViewSummary {
  const records = urls.map((url) => shown[url]).filter((r): r is ShownFromStorage => r !== undefined);
  if (records.length === 0) return { any: false, oldest: null };
  const oldest = records.reduce((a, b) => (new Date(a.moment).getTime() <= new Date(b.moment).getTime() ? a : b));
  return { any: true, oldest };
}

/**
 * Read inside a component. Re-renders when any of `urls`' shown-from-storage state changes.
 *
 * **The derivation happens OUTSIDE the zustand selector, and that is load-bearing rather than
 * stylistic** — the same shape `home.store.ts`'s own `homeGroups.use()` already uses for exactly
 * this reason. Zustand v5's `useStore` calls the selector as `getSnapshot()` with no memoization of
 * its OWN (`node_modules/zustand/react.js`): a selector that builds a fresh object every call —
 * `summarize` does, deliberately, since `oldest` is a plain value — makes `useSyncExternalStore` see
 * a different reference on every read even when nothing in the store changed, which forces a
 * re-render, which reads again, which is different again — React's own "the result of getSnapshot
 * should be cached" warning, and an actual infinite loop once anything else is mounted alongside it.
 * Selecting the STABLE `shown` record (unchanged unless `markShownFromStorage`/
 * `clearShownFromStorage` actually write) and summarising it afterwards, in plain code, is what
 * keeps the selector's own return value referentially stable between real state changes.
 */
export function useShownFromStorage(urls: readonly string[]): StoredViewSummary {
  const shown = store((s) => s.shown);
  return summarize(shown, urls);
}

/** Read outside React. */
export function shownFromStorageNow(urls: readonly string[]): StoredViewSummary {
  return summarize(store.getState().shown, urls);
}

/** Test seam, like every other module-level store here. Production code never calls it. */
export function resetStoredViewsPresentation(): void {
  store.setState({ shown: {} });
  lastCleared.clear();
}
